import { FC, useEffect } from 'react'
import { useFormik } from 'formik'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  useBoardsList,
  useBoardsListPosts,
  useBoardsPostsCreate
} from 'services/boards/boards.hooks'
import OperationsFrozenMessage from 'components/OperationsFrozenMessage'
import { MobileDatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useIntl } from 'react-intl'
import { EventAvailableOutlined, EventBusyOutlined } from '@mui/icons-material'
import { getApiErrorMessage } from 'utils'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import { FieldLabel, primaryButtonSx, submitButtonSx } from 'components/Helpdesk/FormPrimitives'

// New board post form ("Panel Klienta 3.0" Komunikaty — Dodaj do tablicy).
const BoardsNewPost: FC = () => {
  const { data = [] } = useBoardsList()
  const { refetch } = useBoardsListPosts(data.map(({ id }) => id))
  const { createPost, isSuccess, isError, error, reset } = useBoardsPostsCreate()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  // Only boards from non-frozen investments can receive new posts
  const availableBoards = data.filter((board) => !board.investment.operationsFrozen)

  const formik = useFormik<{
    boardId: string
    title: string
    text: ''
    eventDate: Dayjs
  }>({
    initialValues: {
      boardId: '',
      title: '',
      text: '',
      eventDate: dayjs()
    },
    onSubmit: async ({ boardId, text, title, eventDate }, { setSubmitting }) => {
      setSubmitting(true)
      await createPost({
        boardId: Number(boardId),
        text,
        title,
        eventDate: eventDate.toISOString()
      })
      refetch()
      setSubmitting(false)
    }
  })

  const resetForm = () => {
    reset()
    formik.resetForm()
  }

  useEffect(() => {
    if (!!availableBoards.length) {
      formik.setFieldValue('boardId', `${availableBoards[0].id}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pageTitle = formatMessage({
    id: 'boardsNewPost.addToBoard',
    defaultMessage: 'Dodaj do tablicy'
  })

  const header = (
    <>
      <BackNavigationHeader navigateTo='/board' label={pageTitle} />
      {isDesktop && (
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.r4r.navy, mb: '8px' }}>
          {pageTitle}
        </Typography>
      )}
    </>
  )

  if (isError) {
    return (
      <>
        {header}
        <BackNavigationContent>
          <HelpdeskEmptyState
            Icon={EventBusyOutlined}
            title={
              getApiErrorMessage(error) ||
              formatMessage({
                id: 'boardsNewPost.error',
                defaultMessage: 'Nie można dodać wydarzenia spróbuj ponownie później.'
              })
            }
          />
          <Button fullWidth sx={{ ...primaryButtonSx, mt: '16px' }} onClick={resetForm}>
            {formatMessage({ id: 'boardsNewPost.errorBtn', defaultMessage: 'Wróć' })}
          </Button>
        </BackNavigationContent>
      </>
    )
  }

  if (isSuccess) {
    return (
      <>
        {header}
        <BackNavigationContent>
          <HelpdeskEmptyState
            Icon={EventAvailableOutlined}
            title={formatMessage({
              id: 'boardsNewPost.succes',
              defaultMessage: 'Wydarzenie zostało wysłane i oczekuję na akceptację przez moderatora.'
            })}
          />
          <Button fullWidth sx={{ ...primaryButtonSx, mt: '16px' }} onClick={resetForm}>
            {formatMessage({ id: 'boardsNewPost.succesBtn', defaultMessage: 'Dodaj nowe' })}
          </Button>
        </BackNavigationContent>
      </>
    )
  }

  return (
    <>
      {header}
      <BackNavigationContent>
        {availableBoards.length === 0 ? (
          <OperationsFrozenMessage />
        ) : (
          <Box sx={{ pb: '24px' }}>
            <FieldLabel>
              {formatMessage({ id: 'boardsNewPost.form.title', defaultMessage: 'Tytuł' })}
            </FieldLabel>
            <TextField
              name='title'
              onChange={formik.handleChange}
              value={formik.values.title}
              fullWidth
              rows={2}
              multiline
              disabled={formik.isSubmitting}
            />
            <FieldLabel>
              {formatMessage({ id: 'boardsNewPost.form.text', defaultMessage: 'Opis' })}
            </FieldLabel>
            <TextField
              name='text'
              onChange={formik.handleChange}
              value={formik.values.text}
              fullWidth
              rows={5}
              multiline
              disabled={formik.isSubmitting}
            />
            <FieldLabel>
              {formatMessage({ id: 'boardsNewPost.board', defaultMessage: 'Tablica' })}
            </FieldLabel>
            <Select
              fullWidth
              disabled={availableBoards.length <= 1}
              value={formik.values.boardId}
              onChange={(event) => formik.setFieldValue('boardId', event.target.value)}>
              {availableBoards.map(({ id, name, investment }) => (
                <MenuItem key={id} value={id.toString()}>
                  {name} - {investment.name}
                </MenuItem>
              ))}
            </Select>
            <FieldLabel>
              {formatMessage({
                id: 'boardsNewPost.form.eventDate',
                defaultMessage: 'Data wydarzenia'
              })}
            </FieldLabel>
            <MobileDatePicker
              disablePast
              disableHighlightToday
              value={formik.values.eventDate}
              onChange={(value) => {
                formik.setFieldValue('eventDate', value)
              }}
              slotProps={{
                textField: {
                  fullWidth: true
                }
              }}
            />
            <LoadingButton
              onClick={formik.submitForm}
              fullWidth
              loading={formik.isSubmitting}
              sx={{ ...submitButtonSx, mt: '28px' }}>
              {formatMessage({ id: 'boardsNewPost.form.submit', defaultMessage: 'WYŚLIJ' })}
            </LoadingButton>
          </Box>
        )}
      </BackNavigationContent>
    </>
  )
}

export default BoardsNewPost
